// @flow

import { Box, useSystem } from 'react-system';
import { graphql } from 'react-relay';
import Head from 'next/head';
import { type NextQuery, nextQuery } from '../controls/relay';
import { DescriptorStore } from '../controls/router';
import { MuiThemeProvider } from '../controls/mui-providers';
import { Layout } from '../shared/Layout.js';
import { useViewportType } from '../hooks/viewport-type';
import { toAsUrl } from '../utils/rewrites';
import { makeListingHref } from '../shared/AggregatesListings/listing-href';
import { AggregatesListings } from '../shared/AggregatesListings/AggregatesListings.js';
import { LISTINGS_LIST_MAX_WIDTH } from '../shared/AggregatesListings/consts';
import { TopFilters } from '../shared/AggregatesListings/Filters/TopFilters.js';
import {
  listingsDescriptor,
  paramsToVariables,
} from '../shared/AggregatesListings/listingsDescriptor';
import { ListingsStateProvider } from '../shared/AggregatesListings/state';
import {
  showMapDescriptor,
  offerTypeDescriptor,
} from '../shared/AggregatesListings/Filters/sharedDescriptors';
import { AggregatesListingDialogClient } from '../shared/AggregatesListings/aggregates-listing-dialog-client';
import type { listingsQuery } from './__generated__/listingsQuery.graphql';

const ListingsLayoutHeaderContent = ({ root, page }) => {
  const { media } = useSystem();
  const LOGO_DESKTOP_WIDTH = 160;
  const PADDING = 16;
  // I have no idea why 172px works, as based on my knowledge
  // 176 = LOGO_DESKTOP_WIDTH + PADDINGS should work better, but it's not.
  // Visually on all browsers 172 gives 10x better result vs 176
  // What happens with 4px and where they come from I don't know
  const HAND_PICKED_UP_COST = 172;

  return (
    <Box
      flexShrink={0}
      pr={3}
      css={media({
        // Max width should be aligned when max width of listings card achieved
        maxWidth: LISTINGS_LIST_MAX_WIDTH - LOGO_DESKTOP_WIDTH - PADDING * 2,

        width: `calc(62% - ${HAND_PICKED_UP_COST}px)`,
        display: ['none', 'block'],
        // Height of underlying input, needed to align underlying content
        // using top of this box.
        height: '54px',
      })}
    >
      <TopFilters root={root} page={page} />
    </Box>
  );
};

const ListingsPageWithProviders = props => (
  <ListingsStateProvider>
    <DescriptorStore>
      <ListingsPage {...props} />
    </DescriptorStore>
  </ListingsStateProvider>
);

const ListingsPage = props => {
  const viewportType = useViewportType();
  const [{ showMap }] = showMapDescriptor.useState();
  const [{ offerType }] = offerTypeDescriptor.useState();
  const [params] = listingsDescriptor.useState();

  const getLocalizedQuery = mapPlace => {
    const places = props.data.aggregatesListings?.filters.places;
    return places == null || places.length === 0
      ? {}
      : { placeSlugs: JSON.stringify(places.map(mapPlace)) };
  };

  const buyLogoLink = props.data.cms.navigation?.nav_listings_buy_logo_link;
  const rentLogoLink = props.data.cms.navigation?.nav_listings_rent_logo_link;

  const buyLogoHref =
    buyLogoLink?._meta?.uid != null && buyLogoLink.template != null
      ? {
          pathname: `/${buyLogoLink.template}`,
          query: {
            uid: buyLogoLink._meta.uid,
          },
        }
      : null;

  const rentLogoHref =
    rentLogoLink?._meta?.uid != null && rentLogoLink.template != null
      ? {
          pathname: `/${rentLogoLink.template}`,
          query: {
            uid: rentLogoLink._meta.uid,
          },
        }
      : null;

  return (
    <MuiThemeProvider>
      <Layout
        root={props.data}
        pageWithData={props.data.cms.pageWithData}
        headerStyle="listings-page"
        headerContent={
          <ListingsLayoutHeaderContent
            root={props.data}
            page={props.data.cms.pageWithData?.page}
          />
        }
        footerCenterPx={[3, 3, 0]}
        showFooter={
          showMap === false || (showMap == null && viewportType === 'mobile')
        }
        logoHref={offerType === 'buy' ? buyLogoHref : rentLogoHref}
        localeSwitcherQuery={{
          en: getLocalizedQuery(p => ({
            type: p.type,
            slug: p.slugEn,
          })),
          fr: getLocalizedQuery(p => ({
            type: p.type,
            slug: p.slugFr,
          })),
          de: getLocalizedQuery(p => ({
            type: p.type,
            slug: p.slugDe,
          })),
          it: getLocalizedQuery(p => ({
            type: p.type,
            slug: p.slugIt,
          })),
          es: getLocalizedQuery(p => ({
            type: p.type,
            slug: p.slugEs,
          })),
        }}
      >
        <Head>
          {/* PageMeta moved to AggregatesListingsSEOLinks */}
          <style>{`html { overflow-y: scroll; }`}</style>
        </Head>

        <AggregatesListings
          root={props.data}
          page={props.data.cms.pageWithData?.page}
          showFooter={
            (showMap === true || showMap == null) && viewportType !== 'mobile'
          }
        />
        {props.data.aggregatesListings && (
          <AggregatesListingDialogClient
            parentHref={props.router}
            params={params}
            aggregatesListings={props.data.aggregatesListings}
          />
        )}
      </Layout>
    </MuiThemeProvider>
  );
};

const ListingsP: NextQuery<
  listingsQuery,
  {|
    slug?: string,
    compositePropertyType_eq?: string,
    lang?: string,
    uid?: string,
    listingSlug?: string,
  |},
> = nextQuery((ctx, userData) => {
  const params = listingsDescriptor.getState(ctx.query);
  const paramsVariables = paramsToVariables(params, ctx.query);

  const variables = {
    ...paramsVariables,
    includeEn: userData.supportedLanguages.includes('en'),
    includeFr: userData.supportedLanguages.includes('fr'),
    includeDe: userData.supportedLanguages.includes('de'),
    includeIt: userData.supportedLanguages.includes('it'),
    includeEs: userData.supportedLanguages.includes('es'),
  };

  return {
    query: graphql`
      query listingsQuery(
        $sortBy: String
        $sortDirection: String
        $filters: AggregatesListingFiltersInput!
        $page: AggregatesListingFiltersPage
        $slug: String!
        $includePlace: Boolean!
        $includeCountry: Boolean!
        $uid: String!
        $lang: String!
        $lngShort: LangInput!
        $propertyMainType: PropertyMainType
        $numberOfRooms: Float
        $offerType: String!
        $includeEn: Boolean!
        $includeFr: Boolean!
        $includeDe: Boolean!
        $includeIt: Boolean!
        $includeEs: Boolean!
      ) {
        ...Layout_root @arguments(lang: $lang)
        ...AggregatesListings_root
        ...AggregatesListingsFilters_root
        ...TopFilters_root

        aggregatesListings(
          page: $page
          sortBy: $sortBy
          sortDirection: $sortDirection
          filters: $filters
        ) {
          ...aggregatesListingDialogClient_aggregatesListings
          totalCount
          filters {
            places {
              countryCode
              type
              slug
              slugEn: slug(lang: en) @include(if: $includeEn)
              slugFr: slug(lang: fr) @include(if: $includeFr)
              slugDe: slug(lang: de) @include(if: $includeDe)
              slugIt: slug(lang: it) @include(if: $includeIt)
              slugEs: slug(lang: es) @include(if: $includeEs)
            }
          }
        }

        placeBySlug(slug: $slug, lang: $lngShort) @include(if: $includePlace) {
          hasCanonical
          canonicalPlace {
            slug
            lang
          }
          index
        }

        cms {
          pageWithData(uid: $uid, lang: $lang) {
            page {
              ...TopFilters_page
              ...AggregatesListings_page
            }
            ...Layout_pageWithData
          }

          navigation(uid: "main", lang: $lang) {
            nav_listings_buy_logo_link {
              ... on CMSPage {
                template
                _meta {
                  uid
                }
              }
            }
            nav_listings_rent_logo_link {
              ... on CMSPage {
                template
                _meta {
                  uid
                }
              }
            }
          }
        }
      }
    `,
    variables,
    cacheStrategy: 'none',
    validate: data => {
      const {
        featureConfig: { disableAggregator },
      } = userData;

      const dataPlaces = data.aggregatesListings?.filters.places ?? [];
      const queryPlaces = (params.placeSlugs ?? []).filter(Boolean);
      const placeMetaData = data.placeBySlug;
      let redirecthref = null;

      if (disableAggregator) {
        return { type: 'redirect', statusCode: 307, location: '/' };
      }

      // Return 410 on pages with no or less listings.
      if (
        (variables.offerType === 'rent' &&
          ['BUILDING', 'PROP'].includes(variables.propertyMainType)) ||
        (variables.offerType === 'buy' &&
          ['ROOM', 'PARK'].includes(variables.propertyMainType))
      ) {
        return { type: 'error', statusCode: 410 };
      }

      if (placeMetaData?.index === false && placeMetaData.hasCanonical) {
        return {
          type: 'redirect',
          statusCode: 301,
          location: toAsUrl(
            userData.routes,
            userData.language,
            {
              pathname: '/listings',
              query: {
                ...ctx.query,
                placeSlugs: JSON.stringify([
                  {
                    slug: placeMetaData.canonicalPlace?.slug ?? null,
                    lang: placeMetaData.canonicalPlace?.lang ?? null,
                  },
                ]),
              },
            },
            {
              pathname: '/listings',
              query: ctx.query,
            },
          ).as,
          page: 'listings',
        };
      }

      // redirect if rooms exist for property types without room filter
      if (
        !['HOUSE_APPT', 'HOUSE', 'APPT'].includes(variables.propertyMainType) &&
        variables.numberOfRooms != null &&
        variables.propertyMainType != null
      ) {
        redirecthref = makeListingHref({
          offerType: variables.offerType,
          slug: variables.slug,
          compositePropertyType: variables.propertyMainType,
        });
      }

      if (redirecthref != null) {
        // If we try to open https://realadvisor.ch/en/buy/plot/5-rooms directly
        // in browser it should return 404
        if (userData.landingPageUrl === ctx.asPath) {
          return { type: 'error', statusCode: 404 };
        }

        // Otherwise redirect to correct page. For example, when selecting Plot
        // property type when we have currently selected House - 5 rooms, it will redirect
        // to Plot property type without rooms filter.
        const location = toAsUrl(
          userData.routes,
          userData.language,
          redirecthref,
          {
            pathname: '/listings',
            query: ctx.query,
          },
        );

        return { type: 'redirect', statusCode: 302, location: location.as };
      }

      // we want to show 404 if places' slugs are in a wrong language or don't exist
      if (
        queryPlaces.length !== dataPlaces.length ||
        queryPlaces.some(
          qp =>
            dataPlaces.some(
              dp =>
                dp.slug === qp.slug && dp.countryCode === userData.countryCode,
            ) === false,
        )
      ) {
        return { type: 'error', statusCode: 404 };
      }

      // we want to redirect to page-0 if pagination has 0 results
      if (
        paramsVariables.page != null &&
        data.aggregatesListings != null &&
        data.aggregatesListings.totalCount <
          paramsVariables.page.number * paramsVariables.page.count
      ) {
        const params = listingsDescriptor.getState(ctx.query);
        const href = {
          pathname: '/listings',
          query: listingsDescriptor.getQueryParams({
            ...params,
            page: 0,
          }),
        };

        const location = toAsUrl(userData.routes, userData.language, href, {
          pathname: '/listings',
          query: ctx.query,
        });

        return { type: 'redirect', statusCode: 302, location: location.as };
      }

      // redirect to canonical url if current url doesn't match canonical url
      // for example:
      // current path is /fr/louer/parking/1232-confignon
      // canonical path is /fr/louer/1232-confignon/parking
      // we should redirect to /fr/louer/1232-confignon/parking
      const canonicalPath = toAsUrl(
        userData.routes,
        userData.language,
        ctx.asPath,
        {
          pathname: '/listings',
          query: ctx.query,
        },
      ).as;
      if (canonicalPath !== ctx.asPath) {
        return {
          type: 'redirect',
          statusCode: 301,
          location: canonicalPath,
          page: 'listings',
        };
      }
      return null;
    },
  };
})(ListingsPageWithProviders);

export default ListingsP;
